import type { NavigateFunction } from 'react-router-dom'

import { ChannelAvatar, SettingsIcon } from '@buffer-mono/popcorn'

import type { MainSidebarChannel } from '~publish/components/MainSidebar/components/ChannelsSection/useChannels'

import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction, ActionContext } from './types'

const SERVICE_ALIASES: Record<string, string[]> = {
  instagram: ['ig', 'insta', 'gram'],
  facebook: ['fb', 'meta'],
  twitter: ['x', 'tweet', 'tweets'],
  linkedin: ['li', 'linked'],
  pinterest: ['pin', 'pins'],
  tiktok: ['tt', 'tok'],
  youtube: ['yt', 'video'],
  threads: ['thread'],
  mastodon: ['masto', 'fedi', 'fediverse'],
  bluesky: ['bsky', 'sky'],
  googlebusiness: ['google', 'gbp', 'gmb', 'business'],
  startPage: ['start', 'landing', 'bio', 'link'],
}

const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'Twitter / X',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  threads: 'Threads',
  mastodon: 'Mastodon',
  bluesky: 'Bluesky',
  googlebusiness: 'Google Business Profiles',
  startPage: 'Start Page',
}

function getServiceDisplayName(service: string): string {
  return (
    SERVICE_DISPLAY_NAMES[service] ??
    service.charAt(0).toUpperCase() + service.slice(1)
  )
}

/**
 * Channel sub-navigation items.
 *
 * - `keywords`: Used for search matching within the action's context (e.g., when
 *   a channel is already matched). These are more specific terms.
 * - `filterKeywords`: Used for display mode filtering to determine if a submenu
 *   should be shown. When the query matches any filterKeyword, only that submenu
 *   type is shown across all channels (filtered mode).
 */
const CHANNEL_SUBMENUS = [
  {
    path: '',
    label: 'Schedule',
    keywords: ['queue', 'scheduled', 'posts'],
    filterKeywords: ['schedule', 'queue', 'scheduled', 'posts'],
  },
  {
    path: 'comments',
    label: 'Community',
    keywords: ['comments', 'engage', 'replies'],
    filterKeywords: ['community', 'comments', 'engage', 'replies', 'mentions'],
  },
  {
    path: 'analytics',
    label: 'Analytics',
    keywords: ['insights', 'metrics', 'performance'],
    filterKeywords: [
      'analytics',
      'insights',
      'metrics',
      'stats',
      'performance',
    ],
  },
]

const INSTAGRAM_BUSINESS_SUBMENUS = [
  {
    path: 'shop-grid',
    label: 'Grid',
    keywords: ['shop', 'grid', 'instagram', 'preview'],
    filterKeywords: ['grid', 'shop', 'preview'],
  },
]

type SubmenuType = (typeof CHANNEL_SUBMENUS)[number]

/**
 * Channel display modes based on search query.
 * - collapsed: No query - show primary channel only, hide submenus
 * - expanded: Query matches channel - show all submenus for that channel
 * - filtered: Query matches submenu keyword - show all channels with matching submenu only
 */
type DisplayMode = 'collapsed' | 'expanded' | 'filtered'

function getAllSubmenuFilterKeywords(): string[] {
  const allKeywords: string[] = []
  for (const submenu of CHANNEL_SUBMENUS) {
    allKeywords.push(...submenu.filterKeywords)
  }
  for (const submenu of INSTAGRAM_BUSINESS_SUBMENUS) {
    allKeywords.push(...submenu.filterKeywords)
  }
  return allKeywords
}

function submenuMatchesFilter(submenu: SubmenuType, query: string): boolean {
  const normalizedQuery = query.toLowerCase().trim()
  return submenu.filterKeywords.some(
    (keyword) =>
      keyword.startsWith(normalizedQuery) || normalizedQuery.includes(keyword),
  )
}

const MIN_FILTERED_QUERY_LENGTH = 3

function getDisplayMode(query: string): DisplayMode {
  if (!query.trim()) {
    return 'collapsed'
  }

  const normalizedQuery = query.toLowerCase().trim()

  if (normalizedQuery.length < MIN_FILTERED_QUERY_LENGTH) {
    return 'expanded'
  }

  const submenuKeywords = getAllSubmenuFilterKeywords()

  const isSubmenuKeyword = submenuKeywords.some(
    (keyword) =>
      keyword.startsWith(normalizedQuery) || normalizedQuery.includes(keyword),
  )

  if (isSubmenuKeyword) {
    return 'filtered'
  }

  // Default to expanded (search algorithm will handle matching)
  return 'expanded'
}

/**
 * Options for building channel-related actions.
 */
interface BuildChannelActionsOptions {
  /** List of channels to create actions for */
  channels: MainSidebarChannel[]
  /** React Router navigate function for navigation */
  navigate: NavigateFunction
  /** Current page context for smart navigation */
  currentContext?: ActionContext
  /** Whether the user has exactly one channel */
  isSingleChannelMode?: boolean
  /** Current search query for display mode filtering */
  query?: string
  /** Returns the number of unreplied comments for a channel, or null if unsupported */
  getUnrepliedCommentsCount?: (channelId: string) => number | null
}

function getContextAwareChannelUrl(
  channelId: string,
  currentContext: ActionContext | undefined,
): string {
  switch (currentContext) {
    case 'calendar-view':
      return `/channels/${channelId}/calendar/week`
    case 'community-view':
      return `/channels/${channelId}/community`
    case 'analytics-view':
      return `/channels/${channelId}/analytics`
    default:
      return `/channels/${channelId}`
  }
}

function getServiceAliases(service: string): string[] {
  return SERVICE_ALIASES[service] ?? []
}

function getChannelSubmenus(
  channel: MainSidebarChannel,
): typeof CHANNEL_SUBMENUS {
  const submenus = [...CHANNEL_SUBMENUS]

  if (channel.service === 'instagram' && channel.type === 'business') {
    submenus.push(...INSTAGRAM_BUSINESS_SUBMENUS)
  }

  return submenus
}

/**
 * Builds command bar actions for all user channels.
 *
 * Supports three display modes based on search query:
 * - Collapsed: No query - shows only primary channel actions
 * - Expanded: Query matches channel - shows channel with all submenus
 * - Filtered: Query matches submenu keyword - shows that submenu across all channels
 *
 * @param options - Configuration options
 * @param options.channels - List of channels to create actions for
 * @param options.navigate - React Router navigate function
 * @param options.currentContext - Current page context for smart navigation
 * @param options.isSingleChannelMode - If true, promotes submenus to navigation section
 * @param options.query - Current search query for display mode filtering
 * @returns Array of command bar actions for channels
 */
export function buildChannelActions({
  channels,
  navigate,
  currentContext,
  isSingleChannelMode = false,
  query = '',
  getUnrepliedCommentsCount,
}: BuildChannelActionsOptions): CommandBarAction[] {
  const actions: CommandBarAction[] = []

  // In single channel mode, channel views are promoted to navigation section
  // instead of being grouped under channels
  const section = isSingleChannelMode ? 'navigation' : 'channels'

  const displayMode = getDisplayMode(query)

  for (const channel of channels) {
    const submenus = getChannelSubmenus(channel)
    const serviceAliases = getServiceAliases(channel.service)
    const channelName = channel.name || channel.service

    const contextAwareUrl = getContextAwareChannelUrl(
      channel.id,
      currentContext,
    )
    const showPrimaryAction = !isSingleChannelMode && displayMode !== 'filtered'
    const serviceDisplayName = getServiceDisplayName(channel.service)
    const channelUnreadCount =
      getUnrepliedCommentsCount?.(channel.id) ?? undefined

    if (showPrimaryAction) {
      actions.push(
        createAction({
          id: `channel-${channel.id}`,
          name: channelName,
          description: serviceDisplayName,
          keywords: [
            channelName.toLowerCase(),
            channel.service.toLowerCase(),
            ...serviceAliases,
            'channel',
          ],
          icon: (
            <ChannelAvatar
              src={channel.avatar ?? undefined}
              alt={channelName}
              channel={channel.service}
              size="xsmall"
            />
          ),
          section,
          priority: PRIORITIES.channels,
          perform: () => navigate(contextAwareUrl),
          unrepliedCommentsCount: channelUnreadCount,
        }),
      )
    }

    const shouldShowSubmenus =
      displayMode !== 'collapsed' || isSingleChannelMode

    if (shouldShowSubmenus) {
      for (const submenu of submenus) {
        if (
          displayMode === 'filtered' &&
          !submenuMatchesFilter(submenu, query)
        ) {
          continue
        }

        const basePath = `/channels/${channel.id}`
        const fullPath = submenu.path ? `${basePath}/${submenu.path}` : basePath

        // Community URL uses /community path instead of /comments
        const url =
          submenu.path === 'comments'
            ? `/channels/${channel.id}/community`
            : fullPath

        const prefix = isSingleChannelMode ? undefined : channelName

        actions.push(
          createAction({
            id: `channel-${channel.id}-${submenu.path || 'schedule'}`,
            name: submenu.label,
            prefix,
            description: serviceDisplayName,
            keywords: [
              channelName.toLowerCase(),
              channel.service.toLowerCase(),
              ...serviceAliases,
              submenu.label.toLowerCase(),
              ...submenu.keywords,
            ],
            icon: (
              <ChannelAvatar
                src={channel.avatar ?? undefined}
                alt={channelName}
                channel={channel.service}
                size="xsmall"
              />
            ),
            section,
            priority: isSingleChannelMode
              ? PRIORITIES.analytics
              : PRIORITIES.channelSubmenus,
            perform: () => navigate(url),
            unrepliedCommentsCount:
              submenu.path === 'comments' ? channelUnreadCount : undefined,
          }),
        )
      }
    }

    if (isSingleChannelMode) {
      actions.push(
        createAction({
          id: `channel-${channel.id}-settings`,
          name: 'Channel Settings',
          keywords: [
            'settings',
            'configure',
            'manage',
            'channel',
            'preferences',
          ],
          icon: <SettingsIcon />,
          section,
          priority: PRIORITIES.channelSettings,
          description: 'Manage channel settings',
          perform: () => navigate('/settings/channels'),
        }),
      )
    }
  }

  return actions
}
